import type {
  FiatPrice,
  TrackingProperties,
} from "@b2bportal/air-shopping-api";
import type { IIdFlight, Id } from "../common/search";
import type { FareclassShelfBrandName } from "./shop";

export enum FlightSearchQueryParams {
  ORIGIN = "origin",
  DESTINATION = "destination",
  DEPARTURE_DATE = "departureDate",
  RETURN_DATE = "returnDate",
  TRIP_CATEGORY = "tripCategory",
  STOPS_OPTION = "stopsOption",
  ADULT_COUNT = "adultCount",
  CHILD_COUNT = "childCount",
  INFANT_IN_SEAT_COUNT = "infantInSeatCount",
  INFANT_IN_LAP_COUNT = "infantOnLapCount",
  NO_LCC = "noLCC",
  ORIGIN0 = "origin0",
  ORIGIN1 = "origin1",
  ORIGIN2 = "origin2",
  ORIGIN3 = "origin3",
  ORIGIN4 = "origin4",
  DESTINATION0 = "destination0",
  DESTINATION1 = "destination1",
  DESTINATION2 = "destination2",
  DESTINATION3 = "destination3",
  DESTINATION4 = "destination4",
  DEPARTURE_DATE0 = "departureDate0",
  DEPARTURE_DATE1 = "departureDate1",
  DEPARTURE_DATE2 = "departureDate2",
  DEPARTURE_DATE3 = "departureDate3",
  DEPARTURE_DATE4 = "departureDate4",
}

type FlightSearchQueryParamBaseValues = {
  [FlightSearchQueryParams.TRIP_CATEGORY]: string | null;
  [FlightSearchQueryParams.STOPS_OPTION]?: string | null;
  [FlightSearchQueryParams.ADULT_COUNT]: number | null;
  [FlightSearchQueryParams.CHILD_COUNT]: number | null;
  [FlightSearchQueryParams.INFANT_IN_SEAT_COUNT]: number | null;
  [FlightSearchQueryParams.INFANT_IN_LAP_COUNT]: number | null;
  [FlightSearchQueryParams.NO_LCC]?: boolean | null;
};

export type FlightSearchQueryParamValues = FlightSearchQueryParamBaseValues & {
  [FlightSearchQueryParams.ORIGIN]: string;
  [FlightSearchQueryParams.DESTINATION]: string;
  [FlightSearchQueryParams.DEPARTURE_DATE]: string;
  [FlightSearchQueryParams.RETURN_DATE]: string | null;
};

export type MulticityFlightSearchQueryParamValues =
  FlightSearchQueryParamBaseValues & {
    [FlightSearchQueryParams.ORIGIN0]: string;
    [FlightSearchQueryParams.DESTINATION0]: string;
    [FlightSearchQueryParams.DEPARTURE_DATE0]: string;
    [FlightSearchQueryParams.ORIGIN1]: string;
    [FlightSearchQueryParams.DESTINATION1]: string;
    [FlightSearchQueryParams.DEPARTURE_DATE1]: string;
    [FlightSearchQueryParams.ORIGIN2]?: string;
    [FlightSearchQueryParams.DESTINATION2]?: string;
    [FlightSearchQueryParams.DEPARTURE_DATE2]?: string;
    [FlightSearchQueryParams.ORIGIN3]?: string;
    [FlightSearchQueryParams.DESTINATION3]?: string;
    [FlightSearchQueryParams.DEPARTURE_DATE3]?: string;
    [FlightSearchQueryParams.ORIGIN4]?: string;
    [FlightSearchQueryParams.DESTINATION4]?: string;
    [FlightSearchQueryParams.DEPARTURE_DATE4]?: string;
  };

export enum TripCategory {
  ONE_WAY = "one_way",
  ROUND_TRIP = "round_trip",
}

export interface IAirlineOptions {
  label: string;
  value: string;
}

export interface IAirportOptions {
  label: string;
  value: string;
}

export interface IFlightNumbersByAirlineCode {
  [key: string]: string[];
}

export interface FlightNumberOption {
  value: {
    airlineCode: string;
    flightNumber: string;
  };
  label: string;
}

export interface ISliceTimeRange {
  departureTimeRange: ITimeRange;
  arrivalTimeRange: ITimeRange;
}

export interface IFilterOptions {
  airlineOptions: IAirlineOptions[];
  airportOptions: IAirportOptions[];
  flightNumberOptions: FlightNumberOption[];
  flightNumbersByAirline: IFlightNumbersByAirlineCode;
  priceMin?: FiatPrice;
  priceMax?: FiatPrice;
}

export enum FareClassEnum {
  basic = "basic",
  standard = "standard",
  enhanced = "enhanced",
  premium = "premium",
  luxury = "luxury",
}

export type FareClassOptionFilter = {
  [key in FareClassEnum]: boolean;
};

export interface IAirlineOptions {
  label: string;
  value: string;
}

export interface IDateBucket {
  legend: string;
  dates: string[];
}

export interface IMonthBucket {
  monthIndex: number;
  dates: IBucketedDate[];
}

export type SerializableMonthBucket = {
  monthIndex: number;
  dates: {
    date: string;
    bucket: number;
  }[];
};

export interface IBucketedDate {
  date: Date;
  bucket: number;
}

export interface IResult {
  id: Id;
  label: string;
  trackingProperties: TrackingProperties;
  subLabel?: string;
}

export type ITripTerminus = IResult & {
  id: IIdFlight;
};

export interface IPassengerCounts {
  adultsCount: number;
  childrenCount: number;
  infantsInSeatCount: number;
  infantsOnLapCount: number;
}

export enum SliceStopCountFilter {
  DEFAULT = "DEFAULT",
  ANY_NUMBER = "ANY_NUMBER",
  NONE = "NONE",
  ONE_OR_LESS = "ONE_OR_LESS",
  TWO_OR_LESS = "TWO_OR_LESS",
}

export type FareclassOptionFilter = {
  [key in FareclassShelfBrandName]: boolean;
};

export interface ITimeRange {
  // note: time is measured in minutes
  min: number;
  max: number;
}

export type AirlineCode = string;

export interface IFlightNumberFilter {
  airlineCode: AirlineCode;
  flightNumber: string;
}

export enum TripFilter {
  NO_FILTER = "NoFilter",
  AND_FILTER = "AndFilter",
  NON_STOP_FILTER = "NonStopFilter",
  SHORT_LAYOVER_FILTER = "ShortLayoverFilter",
  NO_LOW_COST_CARRIER_FILTER = "NoLowCostCarrierFilter",
}

export enum SearchEvents {
  VIEW_CALENDAR = "view_calendar",
  SEARCH_CALENDAR_CHANGED = "search_calendar_changed",
  SEARCH_TRAVELERS_UPDATED = "search_travelers_updated",
}

export enum MobileFlightSearchDialogSteps {
  MainSearch = "MainSearch",
  Calendar = "Calendar",
}

export type FlightPricePrediction = {
  departureMonths: IMonthBucket[];
  priceTags: string[];
  origin?: ITripTerminus;
  destination?: ITripTerminus;
  tripCategory?: TripCategory;
  isLoading: boolean;
};

export interface MulticityRouteValues {
  origin: ITripTerminus | undefined;
  destination: ITripTerminus | undefined;
  departureDate: string | undefined;
}
